import * as React from 'react'

import { Box, Icon, Tag, Text, Touchable } from '@factorialco/deprecated-design-system'
import moment, { Moment } from 'moment'

import { observer } from 'mobx-react'

import CrashLogo from 'modules/core/components/CrashLogo'
import EmptyList from 'modules/core/components/EmptyList'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import CarryOverExplainer from 'modules/timeoff/components/Leaves/CarryOverExplainer'
import OvertimeExplainer from 'modules/timeoff/components/Leaves/OvertimeExplainer'
import AccruedTooltip from 'modules/timeoff/components/Leaves/Overview/LegacyStatsCounter/AccruedTooltip'
import AvailableTooltip from 'modules/timeoff/components/Leaves/Overview/LegacyStatsCounter/AvailableTooltip'
import { CarryOverData } from 'modules/timeoff/components/Leaves/Overview/LegacyStatsCounter/AvailableTooltip/Carryovers'
import SkeletonLoader from 'modules/timeoff/components/Leaves/Overview/LegacyStatsCounter/SkeletonLoader'
import StatColumn from 'modules/timeoff/components/Leaves/Overview/LegacyStatsCounter/StatColumn'
import TakenTooltip from 'modules/timeoff/components/Leaves/Overview/LegacyStatsCounter/TakenTooltip'
import styles from 'modules/timeoff/components/Leaves/Overview/index.module.css'
// TODO: probabily remove this abstraction once this page got migrated to graphql
import {
  getPreviousContract,
  isFutureContract,
  isPastContract,
} from 'modules/timeoff/lib/contractsVersion'
import { useContractsVersion } from 'modules/timeoff/lib/contractsVersionContext'
import { allowanceTypeOptionsLabel } from 'modules/timeoff/lib/dynamicTranslations'
import allowanceIncidences from 'modules/timeoff/store/collections/allowanceIncidences'
import allowanceStats from 'modules/timeoff/store/collections/stats'
import type Allowance from 'modules/timeoff/store/models/Allowance'
import type AllowanceStat from 'modules/timeoff/store/models/AllowanceStat'
import type { Cycle } from 'modules/timeoff/store/models/AllowanceStat'

const Title = ({ children }: { children: React.ReactNode }) => (
  <Box height="s40" justifyContent="center">
    {children}
  </Box>
)

interface Props {
  employeeId: string
  employeeIsTerminated: boolean
  allowance: Allowance
  originalReferenceDate: Moment | undefined
}

const StatsCounter = ({
  employeeId,
  employeeIsTerminated,
  allowance,
  originalReferenceDate,
}: Props) => {
  const today = moment()
  const { startsOn, contract, contracts } = useContractsVersion()

  const [allowanceStat, setAllowanceStat] = React.useState<AllowanceStat | null | undefined>(null)
  const [referenceDate, setReferenceDate] = React.useState<moment.Moment>(
    originalReferenceDate || today
  )

  const { loading } = useAsyncData(() => {
    const stat = allowanceStats.findOrInitializeBy(employeeId, allowance, referenceDate)
    setAllowanceStat(stat)

    return stat.fetch()
  }, [employeeId, allowance.id, referenceDate])

  if (!allowanceStat || loading) return <SkeletonLoader />

  if (!allowanceStat.attributes.cycles.length) {
    return <EmptyList description={i18n.t('time_off.empty')} />
  }

  const setCycle = (cycle: Cycle | null | undefined) => {
    if (!cycle) return

    if (cycle.covers(today)) {
      setReferenceDate(today)
    } else {
      const newReferenceDate = today.isAfter(cycle.end) ? cycle.end : cycle.begin
      setReferenceDate(newReferenceDate)
    }
  }

  let referenceCycle = allowanceStat.cycleByDate(referenceDate)

  // fallback for the employees terminated in the past and/or contracted in the future
  if (!referenceCycle) {
    let newReferenceDate
    if (employeeIsTerminated || isPastContract(startsOn)) {
      referenceCycle = allowanceStat.lastCycle
      newReferenceDate = referenceCycle?.end
    } else {
      const pastContract = isFutureContract(startsOn)
        ? getPreviousContract(contract, contracts)
        : null
      const pastContractFinished =
        pastContract?.endsOn &&
        stringToDate(pastContract?.endsOn, 'backend').isBefore(referenceDate)
      referenceCycle = pastContractFinished ? allowanceStat.lastCycle : allowanceStat.firstCycle
      newReferenceDate = pastContractFinished ? referenceCycle?.end : referenceCycle?.begin
    }

    if (newReferenceDate !== undefined) {
      setReferenceDate(newReferenceDate)
    }
  }

  if (!referenceCycle) return <CrashLogo />

  const prevCycle = allowanceStat.prevCycle(referenceCycle)
  const nextCycle = allowanceStat.nextCycle(referenceCycle)
  const prevNavDisabled = !prevCycle
  const nextNavDisabled = !nextCycle

  const accrualCycle = allowance.isNextCycleAvailability ? prevCycle : referenceCycle

  const stat = allowanceStat.toJS()
  const availableDays = stat.available_days
  // prorated_allowance_days is a fallback if total_accrued_units is undefined
  const accruedUnits = stat.total_accrued_units || stat.prorated_allowance_days
  const outstandingUnits = stat.outstanding_units
  const usedUnitsUntilReferenceDate = stat.used_units_until_reference_date
  const policyAllowance = stat.policy_allowance
  const proratedDays = stat.prorated_allowance_days
  const proratedDaysInDecimal = stat.total_in_decimal
  const usedDays = stat.used_days
  const carryOvers: CarryOverData[] = stat.carry_overs
  const maxBalanceCap = stat.max_balance_cap

  const accruedIncidences = allowanceIncidences.filter((incidence) => {
    const incidenceAllowanceId = incidence.attributes.timeoff_allowance_id

    return allowance.isLifetime
      ? incidenceAllowanceId === allowance.id && incidence.attributes.target_balance == 'accrued'
      : incidenceAllowanceId === allowance.id &&
          referenceCycle?.incidenceDateCheck(incidence.effectiveOn) &&
          incidence.attributes.target_balance == 'accrued'
  })

  const availableIncidences = allowanceIncidences.filter((incidence) => {
    const incidenceAllowanceId = incidence.attributes.timeoff_allowance_id

    return allowance.isLifetime
      ? incidenceAllowanceId === allowance.id && incidence.attributes.target_balance != 'accrued'
      : incidenceAllowanceId === allowance.id &&
          referenceCycle?.incidenceDateCheck(incidence.effectiveOn) &&
          incidence.attributes.target_balance != 'accrued'
  })

  const isMinuteBlockEnabled =
    !allowance.isOvertime &&
    allowance.isHourly &&
    (allowance.isHourHalfDays || allowance.isHourQuarters)

  const StatsRow = () => (
    <Box
      gap="s32"
      component="ul"
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      paddingTop="s32"
      paddingBottom="s24"
      paddingX="s16"
      width="full"
    >
      <StatColumn>
        <Title>
          <AccruedTooltip
            allowance={allowance}
            policyAllowance={policyAllowance}
            proratedDays={proratedDays}
            proratedDaysInDecimal={proratedDaysInDecimal}
            accrualCycle={accrualCycle}
            incidences={accruedIncidences}
            accruedUnits={accruedUnits}
            isMinuteBlockEnabled={isMinuteBlockEnabled}
          />
        </Title>
      </StatColumn>
      <StatColumn>
        <Title>
          <AvailableTooltip
            total={availableDays}
            accrued={accruedUnits}
            carryOvers={carryOvers}
            incidences={availableIncidences}
            allowance={allowance}
            accrualCycle={accrualCycle}
            referenceDate={referenceDate}
            maxBalanceCap={maxBalanceCap}
            isMinuteBlockEnabled={isMinuteBlockEnabled}
          />
        </Title>
      </StatColumn>
      <StatColumn>
        <Title>
          <TakenTooltip
            total={usedDays}
            outstanding={outstandingUnits}
            usedUnitsUntilReferenceDate={usedUnitsUntilReferenceDate}
            allowance={allowance}
            referenceDate={referenceDate}
            isMinuteBlockEnabled={isMinuteBlockEnabled}
          />
        </Title>
      </StatColumn>
    </Box>
  )

  return (
    <Box
      flexDirection="row"
      justifyContent="spaceBetween"
      alignItems="center"
      gap="s16"
      paddingX="s4"
    >
      <Touchable onClick={() => setCycle(prevCycle)} disabled={prevNavDisabled}>
        <Icon.Medium icon="chevron-left" color={prevNavDisabled ? 'grey000White' : undefined} />
      </Touchable>
      <Box
        flexDirection="column"
        border={{
          all: {
            color: 'grey400',
            width: 's1',
            style: 'solid',
          },
        }}
        borderRadius={{
          all: 'abs008',
        }}
        padding="s2"
        background="grey200"
        width="full"
        justifyContent="spaceBetween"
      >
        <Box
          border={{
            bottom: {
              color: 'grey400',
              width: 's1',
              style: 'solid',
            },
          }}
          justifyContent="center"
        >
          <div className={styles.container}>
            {!allowance.isOvertime && (
              <div className={styles.icon}>
                <CarryOverExplainer
                  accruedByTimeWorked={proratedDays}
                  allowance={allowance}
                  cycle={referenceCycle}
                />
              </div>
            )}
            {allowance.isOvertime && (
              <div className={styles.icon}>
                <OvertimeExplainer />
              </div>
            )}
            <Box
              textAlign="center"
              paddingY="s8"
              width="s240"
              style={{
                margin: 'auto',
              }}
            >
              <Text.Normal ellipsis size="200" color="grey1000" weight="medium">
                {allowance.attributes.name}
              </Text.Normal>
              {!allowance.isOvertime && (
                <Text.Normal size="200" color="grey800" whiteSpace="nowrap">
                  {referenceCycle.rangeLabel}
                </Text.Normal>
              )}
            </Box>
            <div className={styles.tag}>
              <Tag type="neutral">
                {allowanceTypeOptionsLabel(allowance.attributes.allowance_type)}
              </Tag>
            </div>
          </div>
        </Box>
        <StatsRow />
      </Box>
      <Touchable onClick={() => setCycle(nextCycle)} disabled={nextNavDisabled}>
        <Icon.Medium icon="chevron-right" color={nextNavDisabled ? 'grey000White' : undefined} />
      </Touchable>
    </Box>
  )
}

export default observer(StatsCounter)
